// =============================================================================
// Iter 536 — Jenis Pengerjaan Badge (SHARED COMPONENT).
// Satu sumber kebenaran untuk klasifikasi jenis pengerjaan per-item di
// seluruh tab Gudang Offline Rido:
//   • Belum/Sudah Dicetak (Cetak Mockup)
//   • Persiapan / ACC Barang
//   • Pantau Produksi
//   • Selesai / Riwayat
//
// SUMBER DATA (dalam urutan prioritas):
//   1. product.process_mode (per-item, ground truth kalau diisi saat input nota)
//   2. order.order_type + order.custom_type (nota-level fallback)
//
// ATURAN KETAT:
//   - Jenis pengerjaan NEVER ditebak dari nama produk.
//   - Data lama tanpa sinyal valid → BELUM DIKETAHUI (bukan POLOSAN).
//   - Nilai SUBLIM dan JAHIT dipertahankan apa adanya (laporkan kebutuhan label).
// =============================================================================
import React from "react";

/**
 * Resolver: mapping deterministic dari (product, order) → label kerja.
 * @returns {{label: string, token: string, color: string}}
 */
export function resolveJenisPengerjaan(product, order) {
  const pmRaw = String(product?.process_mode || "").trim().toLowerCase();
  const otRaw = String(order?.order_type || "").trim().toLowerCase();
  const ctRaw = String(order?.custom_type || "").trim().toLowerCase();

  // ---- Prioritas 1: product.process_mode ----
  if (pmRaw) {
    // Bordir + Sablon kombinasi (cek dulu agar tidak match "bordir" tunggal)
    if (/(bordir).*(sablon|dtf)|(sablon|dtf).*(bordir)/.test(pmRaw)) {
      return { label: "BORDIR + SABLON", token: "bordir_sablon", color: "violet" };
    }
    if (/bordir/.test(pmRaw)) {
      return { label: "BORDIR", token: "bordir", color: "blue" };
    }
    if (/sablon|dtf/.test(pmRaw)) {
      return { label: "SABLON / DTF", token: "sablon_dtf", color: "amber" };
    }
    if (/sublim|full.?print/.test(pmRaw)) {
      // Dipertahankan apa adanya; informasikan ke user.
      return { label: "SUBLIM", token: "sublim", color: "pink" };
    }
    if (/jahit/.test(pmRaw)) {
      // Dipertahankan; biasanya Jahit tetap keluar dari alur Gudang Rido.
      return { label: "JAHIT", token: "jahit", color: "slate" };
    }
    if (/polos/.test(pmRaw)) {
      return { label: "POLOSAN", token: "polosan", color: "emerald" };
    }
    // Nilai process_mode asing — tampilkan apa adanya (uppercase) agar bisa dilacak.
    return { label: pmRaw.toUpperCase(), token: "custom", color: "slate" };
  }

  // ---- Prioritas 2: order.order_type ----
  if (otRaw === "polos") {
    // Order_type Polos positif → POLOSAN
    if (!ctRaw) {
      return { label: "POLOSAN", token: "polosan", color: "emerald" };
    }
  }

  if (otRaw === "custom" && ctRaw) {
    if (/bordir/.test(ctRaw) && /(sablon|dtf)/.test(ctRaw)) {
      return { label: "BORDIR + SABLON", token: "bordir_sablon", color: "violet" };
    }
    if (/bordir/.test(ctRaw)) {
      return { label: "BORDIR", token: "bordir", color: "blue" };
    }
    if (/(sablon|dtf)/.test(ctRaw)) {
      return { label: "SABLON / DTF", token: "sablon_dtf", color: "amber" };
    }
    if (/sublim|full.?print/.test(ctRaw)) {
      return { label: "SUBLIM", token: "sublim", color: "pink" };
    }
  }

  // ---- Fallback: data tidak jelas ----
  // Iter 540 — Label diubah "BELUM TERIDENTIFIKASI" → "BELUM DIKETAHUI"
  // sesuai mandat Owner untuk Pantau Produksi Rido.
  return { label: "BELUM DIKETAHUI", token: "unknown", color: "neutral" };
}

/**
 * Visual badge. Lightweight, kontras terang/gelap, tidak melebar.
 */
export function JenisPengerjaanBadge({ product, order, size = "sm", className = "", testIdPrefix }) {
  const { label, token, color } = resolveJenisPengerjaan(product, order);
  const tid = testIdPrefix ? `${testIdPrefix}-${token}` : `jp-badge-${token}`;
  const style = {
    display: "inline-flex", alignItems: "center",
    padding: size === "xs" ? "1px 6px" : "2px 8px",
    borderRadius: 999,
    fontSize: size === "xs" ? 10 : 11,
    fontWeight: 700, letterSpacing: 0.2,
    whiteSpace: "nowrap",
    lineHeight: 1.4,
    verticalAlign: "middle",
    // Warna via CSS class token agar bisa dark-mode via :root
  };
  return (
    <span
      className={`jp-badge jp-badge-${color} ${className}`}
      style={style}
      data-testid={tid}
      data-jp-token={token}
      title={`Jenis pengerjaan: ${label}`}
    >
      {label}
    </span>
  );
}

export default JenisPengerjaanBadge;
